import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Receipt, Coffee, X } from 'lucide-react';
import toast from 'react-hot-toast';
import orderService from '../../services/orderService';
import { getProductImageUrl } from '../../utils/imageUtils';

const OrdersTab = () => {
  const { t } = useTranslation();
  const [orders, setOrders] = useState([]);
  const [ordersLoading, setOrdersLoading] = useState(false);
  const [selectedOrderStatus, setSelectedOrderStatus] = useState('all');
  const [selectedOrderForDetails, setSelectedOrderForDetails] = useState(null);

  const loadOrders = useCallback(async () => {
    setOrdersLoading(true);
    try {
      const result = await orderService.getShopOrders();
      if (result.success) setOrders(result.data); else toast.error(result.error || t('dashboard.failedToLoadOrders'));
    } catch {
      toast.error(t('dashboard.failedToLoadOrders'));
    } finally {
      setOrdersLoading(false);
    }
  }, [t]);

  useEffect(() => { loadOrders(); }, [loadOrders]);

  const getStatusColor = (status) => ({ PENDING:'bg-yellow-600', PREPARING:'bg-blue-600', READY:'bg-green-600', COMPLETED:'bg-gray-600', CANCELLED:'bg-red-600' }[status] || 'bg-gray-600');
  const getStatusText = (status) => ({ PENDING:t('dashboard.pending'), PREPARING:t('dashboard.preparing'), READY:t('dashboard.ready'), COMPLETED:t('dashboard.completed'), CANCELLED:t('dashboard.cancelled') }[status] || status);
  const formatDate = (d) => new Date(d).toLocaleDateString('en-US',{ month:'short', day:'numeric', hour:'2-digit', minute:'2-digit'});
  const formatTime = (d) => new Date(d).toLocaleTimeString('en-US',{ hour:'2-digit', minute:'2-digit'});
  const formatDateTime = (d) => new Date(d).toLocaleString('en-US',{ year:'numeric', month:'short', day:'numeric', hour:'2-digit', minute:'2-digit'});
  const getOrderTotal = (order) => order.items.reduce((total,item)=> total + item.totalPrice,0);

  const filteredOrders = orders.filter(o => selectedOrderStatus==='all' || o.status === selectedOrderStatus);

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold text-white">{t('dashboard.ordersManagement')}</h2>
        <button onClick={loadOrders} className="btn-primary flex items-center space-x-2">
          <Receipt className="w-4 h-4" />
          <span>{t('dashboard.refreshOrders')}</span>
        </button>
      </div>
      <div className="flex flex-wrap gap-2 mb-6">
        {['all','PENDING','PREPARING','READY','COMPLETED','CANCELLED'].map(status => {
          const count = status==='all'? orders.length : orders.filter(o=>o.status===status).length;
          return (
            <button key={status} onClick={()=>setSelectedOrderStatus(status)} className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${selectedOrderStatus===status ? 'bg-[#a15507] text-white':'bg-[#1f2937]/50 text-gray-300 hover:text-white'}`}>
              {status==='all'? t('dashboard.allOrders'): getStatusText(status)}<span className="ml-1 text-xs opacity-75">({count})</span>
            </button>
          );
        })}
      </div>
      {ordersLoading ? (
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#a15507] mx-auto mb-4" />
          <p className="text-gray-300">{t('dashboard.loadingOrders')}</p>
        </div>
      ) : filteredOrders.length === 0 ? (
        <div className="text-center py-12">
          <Coffee className="w-16 h-16 text-gray-600 mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-white mb-2">{t('dashboard.noOrders')}</h3>
          <p className="text-gray-400">{selectedOrderStatus==='all' ? t('dashboard.noOrders'): `${t('dashboard.noOrders')} ${t('dashboard.withStatus')} "${getStatusText(selectedOrderStatus)}".`}</p>
        </div>
      ) : (
        <div className="space-y-4">
          {filteredOrders.map(order => (
            <div key={order.id} className="card cursor-pointer hover:bg-[#374151]/70 transition-colors" onClick={()=>setSelectedOrderForDetails(order)}>
              <div className="flex justify-between items-start mb-4">
                <div>
                  <h3 className="text-lg font-semibold text-white">{t('dashboard.orderNumber', { number: order.id })}</h3>
                  <p className="text-gray-300">{t('dashboard.table')} {order.tableNumber} • {formatDate(order.createdAt)}</p>
                </div>
                <div className="flex items-center space-x-3">
                  <span className={`px-3 py-1 rounded-full text-xs font-semibold text-white ${getStatusColor(order.status)}`}>{getStatusText(order.status)}</span>
                  <span className="text-[#a15507] font-bold text-lg">${order.totalAmount.toFixed(2)}</span>
                </div>
              </div>
              <div className="space-y-3 mb-4">
                {order.items.map((it, idx) => (
                  <div key={idx} className="flex justify-between items-center">
                    <div>
                      <p className="text-white font-medium">{it.productName}</p>
                      <p className="text-gray-400 text-sm">Qty: {it.quantity} • ${it.unitPrice.toFixed(2)} each</p>
                    </div>
                    <span className="text-white font-semibold">${it.totalPrice.toFixed(2)}</span>
                  </div>
                ))}
              </div>
              <div className="border-t border-gray-700 pt-4 flex justify-between items-center text-sm text-gray-400">
                <span>{t('dashboard.orderTime')}: {formatTime(order.createdAt)}</span>
                <span>{t('dashboard.totalItems')}: {order.items.length}</span>
              </div>
            </div>
          ))}
        </div>
      )}
      {selectedOrderForDetails && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-[#1f2937] rounded-xl w-full max-w-4xl max-h-[90vh] overflow-hidden border border-gray-700/50">
            <div className="flex items-center justify-between p-6 border-b border-gray-700/50">
              <div className="flex items-center space-x-3">
                <Receipt className="w-8 h-8 text-[#a15507]" />
                <h2 className="text-2xl font-bold text-white">{t('dashboard.orderDetails')}</h2>
              </div>
              <button onClick={()=>setSelectedOrderForDetails(null)} className="text-gray-400 hover:text-white transition-colors"><X className="w-6 h-6" /></button>
            </div>
            <div className="p-6 overflow-y-auto max-h-[70vh]">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <div className="bg-[#374151]/50 rounded-lg p-4">
                    <h4 className="text-white font-semibold mb-3">Order Information</h4>
                    <div className="space-y-2 text-sm">
                      <InfoRow label="Order ID:" value={`#${selectedOrderForDetails.id}`} />
                      <InfoRow label="Table Number:" value={selectedOrderForDetails.tableNumber} />
                      <div className="flex justify-between"><span className="text-gray-400">Status:</span><span className={`px-2 py-1 rounded-full text-xs font-semibold text-white ${getStatusColor(selectedOrderForDetails.status)}`}>{getStatusText(selectedOrderForDetails.status)}</span></div>
                      <div className="flex justify-between"><span className="text-gray-400">Origin:</span><span className={`px-2 py-1 rounded-full text-xs font-semibold text-white ${selectedOrderForDetails.origin==='QR'?'bg-blue-600':'bg-purple-600'}`}>{selectedOrderForDetails.origin}</span></div>
                      <InfoRow label="Created:" value={formatDateTime(selectedOrderForDetails.createdAt)} />
                      {selectedOrderForDetails.updatedAt && <InfoRow label="Last Updated:" value={formatDateTime(selectedOrderForDetails.updatedAt)} />}
                    </div>
                  </div>
                  {selectedOrderForDetails.user && (
                    <div className="bg-[#374151]/50 rounded-lg p-4">
                      <h4 className="text-white font-semibold mb-3">Customer Information</h4>
                      <div className="space-y-2 text-sm">
                        <InfoRow label="Name:" value={`${selectedOrderForDetails.user.firstName} ${selectedOrderForDetails.user.lastName}`} />
                        <InfoRow label="Email:" value={selectedOrderForDetails.user.email} />
                        <InfoRow label="Role:" value={selectedOrderForDetails.user.role} />
                      </div>
                    </div>
                  )}
                </div>
                <div className="space-y-4">
                  <div className="bg-[#374151]/50 rounded-lg p-4">
                    <h4 className="text-white font-semibold mb-3">{t('dashboard.orderItems')}</h4>
                    <div className="space-y-3">
                      {selectedOrderForDetails.items.map((item, idx) => (
                        <div key={idx} className="flex items-center justify-between bg-[#1f2937]/50 rounded-lg p-3">
                          <div className="flex items-center space-x-3">
                            {item.product?.imageUrl && <img src={getProductImageUrl(item.product.imageUrl)} alt={item.product.name} className="w-12 h-12 rounded-lg object-cover" onError={(e)=>{e.target.style.display='none';}} />}
                            <div>
                              <p className="text-white font-medium">{item.productName || item.product?.name}</p>
                              <p className="text-gray-400 text-sm">${item.unitPrice} {t('dashboard.each')}</p>
                            </div>
                          </div>
                          <div className="text-end">
                            <p className="text-white font-semibold">{t('dashboard.quantity')}: {item.quantity}</p>
                            <p className="text-[#a15507] font-bold">${item.totalPrice.toFixed(2)}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                  <div className="bg-[#374151]/50 rounded-lg p-4">
                    <h4 className="text-white font-semibold mb-3">{t('dashboard.orderSummary')}</h4>
                    <div className="space-y-2 text-sm">
                      <InfoRow label={`${t('dashboard.totalItems')}:`} value={selectedOrderForDetails.items.reduce((tot,it)=>tot+it.quantity,0)} />
                      <InfoRow label={`${t('dashboard.subtotal')}:`} value={`$${getOrderTotal(selectedOrderForDetails).toFixed(2)}`} />
                      <div className="flex justify-between border-t border-gray-600 pt-2">
                        <span className="text-white font-semibold">{t('dashboard.total')}:</span>
                        <span className="text-[#a15507] font-bold text-lg">${getOrderTotal(selectedOrderForDetails).toFixed(2)}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const InfoRow = ({ label, value }) => (
  <div className="flex justify-between"><span className="text-gray-400">{label}</span><span className="text-white">{value}</span></div>
);

export default OrdersTab;
